import { Component, EventEmitter, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { GridOptions } from 'ag-grid-community';
import { BPProfilesTicketsDataService, IBPTicketResponse, TicketStatus } from './bp-profiles-tickets-data.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { IColDef } from '../../../../shared/report/report-grid.service';
import _ from 'underscore';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { ITicket } from '../../../../shared/ticket/ticket-data.service';
import { util } from '../../../../shared/util';
import { GaAgGridService } from '../../../../shared/ag-grid-components/ga-ag-grid.service';
import { CellRendererComp } from '../../../../shared/ag-grid-components/ga-ag-grid.component';
import { AgPaginationComponent, AG_PAGINATION_KEY, IAgPaginationParams } from '../../../../shared/ag-grid-components/ag-pagination/ag-pagination.component';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { GaMessageService, IDialog } from '../../../../shared/message/ga-message.service';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '../../../../shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GRID_BP_TICKETS } from '@app/shared/app-grids';

interface ITicketFilterModel {
  ticketStatus?:string;
}

enum GridColumn {
  ID = 'id',
  STATUS = 'status',
  URGENCY = 'urgency',
  DESCRIPTION = 'description',
  CATEGORY = 'category',
  OWNER = 'owner'
}

const gridHeightOffset:string = '40px;.ui-layout-north;.ui-layout-resizer-north;.filter-fields;.fixed-to-top-container;.new-ticket-button';

@Component({
  selector: 'bp-profile-tickets',
  template: `
    <div class="filter-fields">
      <formly [form]="filterForm" [fields]="filterFields" [model]="filterModel"></formly>
    </div>

    <div class="d-block clear margin-top-sm">
      <button class="btn btn-primary margin-bottom-sm new-ticket-button" (click)="showTicketDialog()" translate *ngIf="hasWritePermission">BPPROFILES.TICKETS.CREATE_TICKET</button>
      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" fillHeight="${gridHeightOffset}" height="400"></ga-ag-grid>
    </div>
  `,
  styleUrls: ['./bp-profiles-tickets.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesTicketsComponent implements OnInit, OnDestroy {
  public filterForm:UntypedFormGroup = new UntypedFormGroup({});
  public filterFields:IExtraFieldConfigurationObject[];
  public filterModel:ITicketFilterModel = {};
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public hasWritePermission:boolean;
  public gridId:string = GRID_BP_TICKETS;

  private createTicketUrl:string;
  private ticketData:ITicket[];
  private createTicketDialog:IDialog;
  private eventSubscription:Subscription;

  constructor(
    private formlyService:GaFormlyService,
    private translationService:TranslationService,
    private bpProfilesTicketsDataService:BPProfilesTicketsDataService,
    private bpProfilesService:BPProfilesService,
    private gaAgGridService:GaAgGridService,
    private messageService:GaMessageService,
    private eventService:GaEventService,
    private authService:AuthService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.TICKETS);
    // default ticket status
    this.filterModel.ticketStatus = TicketStatus.OPEN;
    // create filter fields
    this.filterFields = this.createFilterFields();
    // initialize ag-grid
    this.gridInitialize();
    // register listener
    this.listen();
  }

  private listen():void {
    this.eventSubscription = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_CLOSE_DIALOG && this.createTicketDialog) {
        this.createTicketDialog.hide();
        this.createTicketDialog = undefined;
        this.loadData();
      }
    });
  }

  private createColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.ID,
        headerName: 'BPPROFILES.TICKETS.GRID.ID',
        width: 80,
        suppressSizeToFit: true,
        cellClass: 'text-center',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, clickFn: this.onEditTicket }]
        }
      },
      {
        field: GridColumn.STATUS,
        headerName: 'BPPROFILES.TICKETS.GRID.STATUS',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, textClass: params.value === TicketStatus.CLOSED ? 'badge bg-success' : 'badge bg-primary' }]
        },
        cellClass: 'text-center',
        width: 100,
        suppressSizeToFit: true
      },
      {
        field: GridColumn.URGENCY,
        headerName: 'BPPROFILES.TICKETS.GRID.URGENCY',
        valueGetter: 'data.urgency && data.urgency.value',
        cellClass: 'text-center',
        width: 100,
        suppressSizeToFit: true,
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value }]
        }
      },
      {
        field: GridColumn.DESCRIPTION,
        headerName: 'BPPROFILES.TICKETS.GRID.DESCRIPTION',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, clickFn: this.onEditTicket }]
        }
      },
      {
        field: GridColumn.CATEGORY,
        headerName: 'BPPROFILES.TICKETS.GRID.TYPE',
        valueGetter: 'data.category && data.category.description',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value }]
        }
      },
      {
        field: GridColumn.OWNER,
        headerName: 'BPPROFILES.TICKETS.GRID.OWNER',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value }]
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);
      colDef.resizable = true;
      colDef.cellRendererParams.showTooltip = true;
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.createColDefs(),
      suppressMovableColumns: true,
      onGridReady: ():void => {
        this.loadData();
      },
      onFirstDataRendered: ():void => {
        this.gaAgGridService.sizeColumnsToFit(this.gridId);
      },
      statusBar: {
        statusPanels: [
          {
            statusPanel: CellRendererComp.PAGINATION,
            key: AG_PAGINATION_KEY,
            statusPanelParams: <IAgPaginationParams>{
              onPageChanged: this.onPageChanged,
              maxSize: 10
            }
          }
        ]
      }
    };
    this.gridInit = true;
  }

  private onPageChanged = (event:PageChangedEvent):void => {
    this.loadData(event.page);
  };

  private loadData(page?:number):void {
    const ticketStatus:string = this.filterModel.ticketStatus !== TicketStatus.ALL ? this.filterModel.ticketStatus : undefined;
    this.gaAgGridService.setGridLoading(this.gridId, true);
    this.bpProfilesTicketsDataService.getBPTickets(this.bpProfilesService.activeProfile?.id, ticketStatus, page || 1)
      .then((res:IBPTicketResponse) => {
        this.createTicketUrl = res.page?.newLink;
        this.ticketData = util.copyAndExtend(res.page?.tickets || []);
        this.gaAgGridService.setRowData(this.gridId, this.ticketData);
        // refresh pagination config
        this.gaAgGridService.getStatusBarComponentInstance<AgPaginationComponent>(this.gridId, AG_PAGINATION_KEY).refreshPagination({ totalItems: res.totalData, currentPage: res.currentPage });
      })
      .finally(() => {
        this.gaAgGridService.setGridLoading(this.gridId, false);
      });
  }

  private createFilterFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aRadio('ticketStatus')
        .withOptions(this.bpProfilesTicketsDataService.getFilterOptions())
        .withLabelProp('label')
        .withValueProp('id')
        .withValueClass('float-end')
        .withoutWrapper()
        .withInline()
        .onChange(() => this.loadData())
        .build()
    ];
  }

  public showTicketDialog(url?:string):void {
    this.createTicketDialog = this.messageService.showGA1Dialog(
      url ? 'BPPROFILES.TICKETS.EDIT_TICKET' : 'BPPROFILES.TICKETS.CREATE_TICKET',
      url || this.createTicketUrl,
      {
        buttons: [
          { label: 'GA.BUTTONS.CANCEL', className: 'btn-default' }
        ]
      }
    );
  }

  private onEditTicket = (event:Event, _onBusy:EventEmitter<boolean>, params:IRichTextRendererParams):void => {
    event.preventDefault();
    this.showTicketDialog(params.data?.editLink);
  };

  public ngOnDestroy():void {
    this.eventSubscription?.unsubscribe();
  }
}